import * as React from 'react';
import styled from 'styled-components';

type DropdownItemProps = {
    id?: string;
    href?: string;
    className?: string;
    target?: '_blank';
    isDisabled?: boolean;
    isActive?: boolean;
    onClick?: (event: React.MouseEvent | React.TouchEvent | React.KeyboardEvent) => void;
    iconLeft?: React.ReactElement;
    iconRight?: React.ReactElement;
    ref?: React.RefObject<HTMLLIElement>;
    children: any;
};

const DropdownLink = styled.a`
    font-size: 14px;
    display: flex;
    color: inherit;
    align-items: center;
    padding: 8px 18px;
    min-height: 24px;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-family: ${({ theme }) => theme.bento.font.primary.family};

    &[data-active='true']:not(&[aria-disabled='true']) {
        background-color: ${({ theme }) => theme.bento.colors.background.weak};
    }

    &[aria-disabled='true'] {
        color: ${({ theme }) => theme.bento.colors.text.weak};
        cursor: default;
    }

    &:hover:not(&[aria-disabled='true']),
    &:focus:not(&[aria-disabled='true']) {
        color: inherit;
        background-color: ${({ theme }) => theme.bento.colors.background.strong};
        text-decoration: none;
        outline: none;
    }
`;

const IconWrap = styled.div<{ position: 'left' | 'right' }>`
    flex: 0;
    color: inherit;
    padding-${({ position }) => (position === 'left' ? 'right' : 'left')}: 12px;
    margin-left: ${({ position }) => (position === 'right' ? 'auto' : '-6px')};
    margin-right: ${({ position }) => (position === 'right' ? '-6px' : '0')};

    & > svg {
        display: block;
    }

    & > svg path {
        fill: currentColor;
    }
`;

export const DropdownItem = React.forwardRef<HTMLAnchorElement, DropdownItemProps>(
    (
        {
            id,
            children,
            href,
            target,
            isActive,
            isDisabled,
            onClick = () => {},
            iconLeft = null,
            className,
            iconRight = null,
        },
        ref,
    ) => (
        <DropdownLink
            id={id}
            ref={ref}
            href={href}
            className={className}
            target={target}
            data-active={isActive}
            aria-disabled={isDisabled}
            onClick={onClick}
            onTouchEnd={onClick}
            onKeyPress={event => event.key === 'Enter' && onClick(event)}
            tabIndex={0}
        >
            {iconLeft && <IconWrap position="left">{iconLeft}</IconWrap>}
            <div>{children}</div>
            {iconRight && <IconWrap position="right">{iconRight}</IconWrap>}
        </DropdownLink>
    ),
);
